import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
// Import BackButton/PageHeader directly from their source modules rather than the
// page-header barrel: the barrel re-exports FavoriteButton, which would pull
// @lottiefiles/dotlottie-react back into the shared entry chunk (#412).
import { PageHeader } from '@/components/v2/page-header/page-header';
import { BackButton } from '@/components/v2/page-header/page-header-buttons';
import styles from './route-pending.module.less';

const messages = [
  'Finding the beat…',
  'Warming up the dance floor…',
  'Practising the next move…',
  'Getting into position…',
  'Just a few more counts…',
];

export function nextLoadingMessage(current: string, random = Math.random) {
  const choices = messages.filter((message) => message !== current);
  return choices[Math.floor(random() * choices.length)]!;
}

export function destinationAllowsBack(pathname: string) {
  return pathname.split('/').filter(Boolean).length > 1;
}

export function RoutePending() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const router = useRouter();
  const navigate = useNavigate();
  const [message, setMessage] = useState(
    () => messages[Math.floor(Math.random() * messages.length)]!
  );

  useEffect(() => {
    const timer = window.setInterval(() => setMessage(nextLoadingMessage), 3000);
    return () => window.clearInterval(timer);
  }, []);

  const showBack = destinationAllowsBack(pathname);

  // This is the defaultPendingComponent: it can be the FIRST thing a cold
  // session renders (e.g. a shared link straight into a deep route), where
  // window.history has nothing to go back to. Route home instead of stranding
  // the user on a dead Back button.
  const handleBack = () => {
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      void navigate({ to: '/', replace: true });
    }
  };

  return (
    // div not <main>: __root.tsx renders the document's single <main> landmark,
    // so a nested <main> here would create a duplicate landmark (a11y, #706).
    <div className={styles.page} aria-busy="true">
      {showBack && <PageHeader leftAction={<BackButton onClick={handleBack} />} />}
      <div className={styles.loader} role="status" aria-label="Loading page">
        {/* CSS-only equalizer beat replaces the dotlottie dancing-man mascot:
            the player chunk (~168KB) used to load on every pending state. */}
        <div className={styles.beat} aria-hidden="true">
          <span className={styles.beatBar} />
          <span className={styles.beatBar} />
          <span className={styles.beatBar} />
        </div>
        <p className={styles.message} aria-hidden="true">
          {message}
        </p>
      </div>
    </div>
  );
}
